import React, {KeyboardEvent, ReactElement} from "react";

import {
  css,
  hideWindowScroll,
  inertContent,
  restoreWindowScroll,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import KEY from "/src/constants/keyboard-keydown-key";

import ModuleBody from "../../layouts/module-body/ModuleBody";
import SearchCommonTerms from "../common-terms/SearchCommonTerms";
import {getKeyboardFocusableElements} from "/src/utils/header-nav";
import SearchInput from "../search-input/SearchInput";

import * as styles from "./search-drawer.module.scss";

interface SearchDrawerProps {
  open: boolean;
  id: string;
  onClose: () => void;
}

const SearchDrawer = (props: SearchDrawerProps): ReactElement | null => {
  const {open, id, onClose} = props;
  const searchDrawerRef = React.useRef<null | HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState<boolean>(false);
  const searchWrapperDrawerRef = React.useRef<null | HTMLDivElement>(null);

  const setSearchDrawerVisibility = () => {
    if (searchDrawerRef.current) {
      searchDrawerRef.current.style.visibility = open ? "visible" : "hidden";
    }
  };

  const handleKeyDownEvent = (e: KeyboardEvent) => {
    /** Description:
    * In the open search panel, notice that the screen reader after focusing on the "Shipping" CTA jumps to
    * the "Sign Up with Flexport" form at the back, listen to the screen reader stating the "First name",
    * "Last Name", "Phone number" and didn't go back to the search icon at the menu bar above.

    * Expected Result:

    * The screen reader should go back to the search button icon and should close the search panel
    * first before jumping to the next component.
    */

    if (searchDrawerRef.current) {
      const {key, shiftKey} = e;

      // get first and last focusable element
      const focusableItems = getKeyboardFocusableElements(
        searchDrawerRef.current
      );
      const firstItem = focusableItems[0];
      const lastItem = focusableItems[focusableItems.length - 1];

      const currentFocusedElement = document.activeElement;

      if (currentFocusedElement && key === KEY.TAB) {
        // handling shift tab
        if (shiftKey) {
          if (currentFocusedElement.isEqualNode(firstItem)) {
            onClose();
          }
        } else if (!shiftKey && currentFocusedElement.isEqualNode(lastItem)) {
          onClose();
        }
      }
    }
  };

  // on mount
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
    if (open) {
      hideWindowScroll();

      // set main content to inert
      inertContent(true);
    }

    return () => {
      restoreWindowScroll();

      inertContent(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (open) {
      hideWindowScroll();

      // set main content to inert
      inertContent(true);
    } else {
      restoreWindowScroll();

      inertContent(false);
    }
  }, [open]);

  if (!mounted) {
    return null;
  }

  return (
    <div
      ref={searchWrapperDrawerRef}
      className={css(styles.searchDrawer, open ? styles.searchDrawerOpen : "")}
    >
      {/* aria alert, for accessibility support */}
      <div
        role="alert"
        id="search-drawer-alert"
        aria-atomic="true"
        className={styles.searchDrawer__alert}
        aria-live="assertive"
      >
        {translate(open ? "Search panel showed" : "Search panel closed")}
      </div>

      <div
        ref={searchDrawerRef}
        role="region"
        tabIndex={-1}
        id={id}
        onKeyDown={handleKeyDownEvent}
        aria-hidden={!open}
        onTransitionEnd={setSearchDrawerVisibility}
      >
        <ModuleBody>
          <SearchInput active={open} onClose={onClose} />

          <SearchCommonTerms
            tabIndex={open ? 0 : -1}
            classes={styles.searchDrawer__commonTerms}
          />
        </ModuleBody>
      </div>
    </div>
  );
};

export default SearchDrawer;
